import React, { useMemo } from 'react'
import { StayForLong } from '@s4l/stayforlong'
import { IntlProvider, useIntl } from 'react-intl'
import type { OnErrorFn } from '@formatjs/intl'
import type { Device, TypedIntlShape, TypedMessageDescriptor } from '../types'

type AppMobileVersionType = string | null

export interface IStayForLongContext {
  domain: StayForLong
  i18n: TypedIntlShape
  device: Device
  mobileAppVersion: AppMobileVersionType
}

export interface StayForLongProviderProps {
  children: React.ReactNode
  locale: string
  messages: Record<string, string>
  device?: Device
  domain?: StayForLong
  mobileAppVersion?: AppMobileVersionType
  /**
   * If true, suppresses MISSING_TRANSLATION errors from react-intl.
   * Useful for tests where not all translations are provided.
   * @default false
   */
  suppressMissingTranslationErrors?: boolean
}

export const StayForLongContext = React.createContext<IStayForLongContext>({
  domain: StayForLong.empty(),
  i18n: {} as TypedIntlShape,
  device: 'mobile',
  mobileAppVersion: null,
})

/**
 * Wraps the intl object to prevent warnings when formatMessage is called with an empty id.
 * Returns empty string for empty ids instead of triggering react-intl warnings.
 */
const wrapIntlWithEmptyIdGuard = (intl: TypedIntlShape): TypedIntlShape => {
  return {
    ...intl,
    formatMessage: (
      descriptor: TypedMessageDescriptor,
      ...args: Parameters<TypedIntlShape['formatMessage']> extends [unknown, ...infer Rest]
        ? Rest
        : never
    ) => {
      if (!descriptor?.id || descriptor.id === '') {
        return descriptor?.defaultMessage?.toString() ?? ''
      }
      return intl.formatMessage(descriptor, ...args)
    },
  }
}

const StayForLongProviderInternal = ({
  children,
  domain,
  device,
  mobileAppVersion,
}: {
  children: React.ReactNode
  domain: StayForLong
  device: Device
  mobileAppVersion: AppMobileVersionType
}) => {
  const rawIntl = useIntl() as unknown as TypedIntlShape
  const intl = useMemo(() => wrapIntlWithEmptyIdGuard(rawIntl), [rawIntl])

  const contextValue = useMemo(
    () => ({ domain, i18n: intl, device, mobileAppVersion }),
    [domain, intl, device, mobileAppVersion]
  )

  return <StayForLongContext.Provider value={contextValue}>{children}</StayForLongContext.Provider>
}

/**
 * Custom error handler for IntlProvider that can suppress MISSING_TRANSLATION errors.
 */
const createIntlErrorHandler = (suppressMissingTranslation: boolean): OnErrorFn => {
  return error => {
    if (suppressMissingTranslation && error.code === 'MISSING_TRANSLATION') {
      return
    }
    // For other errors, log them as warnings (default behavior)
    console.warn(error)
  }
}

export const StayForLongProvider = ({
  children,
  locale,
  messages,
  device = 'mobile',
  domain = StayForLong.empty(),
  mobileAppVersion = null,
  suppressMissingTranslationErrors = process.env.NODE_ENV === 'test',
}: StayForLongProviderProps) => {
  const translations =
    typeof window !== 'undefined' && window?.__NEXT_DATA__?.props?.pageProps?.translations
      ? window?.__NEXT_DATA__?.props?.pageProps?.translations
      : messages

  if (!translations) {
    throw new Error('Translations messages are not provided either by window or by ssr parameter')
  }
  const onError = useMemo(
    () => createIntlErrorHandler(suppressMissingTranslationErrors),
    [suppressMissingTranslationErrors]
  )

  return (
    <IntlProvider locale={locale} messages={translations} onError={onError}>
      <StayForLongProviderInternal
        domain={domain}
        device={device}
        mobileAppVersion={mobileAppVersion}
      >
        {children}
      </StayForLongProviderInternal>
    </IntlProvider>
  )
}

export const useS4LContext = () => React.useContext<IStayForLongContext>(StayForLongContext)
